/**
 * Atoll shared API functions for all Atoll-compliant frontends.
 * Event registration, event push, and response validation.
 *
 * Moved from services/atollapi.ts — only the functions that are
 * shared across all platforms. Sputnik-specific functions remain
 * in services/atollapi.ts.
 */

import { getAtollServiceURL, getAtollErrorCallback } from "./init";
import { fnAJAXFetch } from "./atollFetch";
import { AJAXSettings } from "../types/ajaxSettings";
import { setSessionID, setCurrentEventListeners } from "./redux/shuteSlice";
import { APIServerResponse, AJAXOutcome, ShuteMessage } from "./types/apiTypes";

export type { APIServerResponse, AJAXOutcome, ShuteMessage } from "./types/apiTypes";

/**
 * Register or unregister event listeners with Atoll's shute server.
 * Calls PUT /events/register.
 */
export async function registerEventListeners(
    psPage: string,
    ptWatchEvents: string[],
    psWatchEventAction: ('add' | 'remove') = 'add',
    sSessionID: string,
    dispatch: any,
    psPageSubContext: string,
    ptPageEvents?: any
): Promise<boolean> {
    const tAjaxParams = {
        watchevents: ptWatchEvents,
        watchaction: psWatchEventAction || 'add',
        watchpage: psPage,
        sputniksessionid: sSessionID, // protocol field — server expects this name
    };
    let bSuccess = false;
    const tAJAXSettings: AJAXSettings = {
        method: "PUT",
        url: `${getAtollServiceURL()}events/register`,
        returnContentType: "json",
        data: tAjaxParams,
        outcome_callback: function (success: boolean, status: number, value: any, headers: any) {
            try {
                if (value?.outcome && success) {
                    if (value?.content?.sessionid) dispatch(setSessionID(value?.content?.sessionid));
                    dispatch(setCurrentEventListeners(value?.content?.watchedevents || []));
                    bSuccess = true;
                } else {
                    throw new Error(`Callback register events outcome false: ${JSON.stringify(value)}`);
                }
            } catch (oErr) {
                console.error(oErr);
            }
        },
        dispatch: dispatch,
    };

    fnAJAXFetch(tAJAXSettings);

    return bSuccess;
}

/**
 * Push an event to connected clients via Atoll's secure event push.
 * Calls PUT /events/securepush.
 */
export async function fnPushEvent(
    ptEvent: ShuteMessage,
    dispatch: any
): Promise<{ success: boolean; data: any }> {
    var bSuccess: boolean = false;
    var oValue: any = null;
    var tAJAXSettings: AJAXSettings = {
        method: "PUT",
        url: `${getAtollServiceURL()}events/securepush`,
        returnContentType: "json",
        data: { ptEvent },
        mode: "cors",
        credentials: "include",
        outcome_callback: function (success: boolean, status: number, value: any, headers: any) {
            bSuccess = success;
            oValue = value;
        },
        dispatch: dispatch
    };

    await fnAJAXFetch(tAJAXSettings);

    return { success: bSuccess, data: oValue };
}

/**
 * Validate an API server response — checks finalstate for errors.
 */
export function fnValidAPIResponse(ptReturn: APIServerResponse): boolean {
    if (ptReturn.finalstate?.error) {
        if (ptReturn.outcome) {
            return true;
        }
        return false;
    }
    return true;
}

/**
 * Validate an AJAX outcome — calls the error callback on failure.
 */
export function fnValidAJAXOutcome(ptReturn: AJAXOutcome): boolean {
    if (ptReturn.ajax_success) {
        return true;
    } else {
        return getAtollErrorCallback()(ptReturn);
    }
}
